import { useEffect, useRef } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export const useLoginTracker = () => {
  const { user, session } = useAuth();
  const trackedSessionsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (user && session) {
      const sessionKey = `${user.id}-${session.access_token.substring(0, 16)}`;
      
      // Only track if we haven't already tracked this session
      if (!trackedSessionsRef.current.has(sessionKey)) {
        trackedSessionsRef.current.add(sessionKey);
        trackLoginSession(user.id, session.access_token);
      }
    }
  }, [user, session]);

  const trackLoginSession = async (userId: string, sessionToken: string) => {
    try {
      console.log('Tracking login session for authenticated user');
      
      // Call the track-login-session edge function
      const { data, error } = await supabase.functions.invoke('track-login-session', {
        body: {
          user_id: userId,
          session_id: sessionToken.substring(0, 16) // Use first 16 chars as session ID
        }
      });

      if (error) {
        console.error('Failed to track login session:', error);
        return;
      }

      if (data?.is_suspicious) {
        console.warn('Suspicious login detected - monitoring user activity');
        toast.warning(
          'Unusual login detected from a new location',
          {
            description: `Login from ${data.location?.city || 'unknown'}, ${data.location?.country || 'unknown'}`,
            duration: Infinity,
            action: {
              label: "This was me",
              onClick: async () => {
                try {
                  await supabase.functions.invoke('dismiss-login-alert', {
                    body: { login_session_id: data.login_session_id }
                  });
                  toast.success('Alert dismissed');
                } catch (error) {
                  console.error('Failed to dismiss alert:', error);
                  toast.error('Failed to dismiss alert');
                }
              }
            },
            cancel: {
              label: "Review",
              onClick: () => {
                window.location.href = '/security-dashboard';
              }
            }
          }
        );
      }

      // Also check for multiple country logins
      try {
        const { data: suspiciousCheck } = await supabase.functions.invoke('check-suspicious-login', {
          body: { user_id: userId }
        });

        if (suspiciousCheck?.is_suspicious) {
          toast.error(
            'Security Alert: Suspicious Activity Detected',
            {
              description: suspiciousCheck.details?.message || 'Multiple login locations detected',
              duration: Infinity,
              action: {
                label: "Review Activity",
                onClick: () => {
                  window.location.href = '/security-dashboard';
                }
              }
            }
          );
        }
      } catch (checkError) {
        console.error('Error checking suspicious login:', checkError);
      }

      console.log('Login session tracked successfully');
    } catch (error) {
      console.error('Error tracking login session:', error);
    }
  };

  return { trackLoginSession };
};